import i18next from 'i18next';
import type { i18n as i18nType } from 'i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ms';
import 'dayjs/locale/ta';
import 'dayjs/locale/zh-cn';
import { writable } from 'svelte/store';
import enUsTranslation from './locales/en-US/translation.json';
import zhCnTranslation from './locales/zh-CN/translation.json';
import msMyTranslation from './locales/ms-MY/translation.json';
import taInTranslation from './locales/ta-IN/translation.json';

export const SUPPORTED_LANGUAGE_CODES = ['en-US', 'zh-CN', 'ms-MY', 'ta-IN'] as const;
export const DEFAULT_LANGUAGE = 'en-US';

const TRANSLATION_RESOURCES = {
	'en-US': {
		translation: enUsTranslation
	},
	'zh-CN': {
		translation: zhCnTranslation
	},
	'ms-MY': {
		translation: msMyTranslation
	},
	'ta-IN': {
		translation: taInTranslation
	}
} as const;

const DAYJS_LANGUAGE_MAP: Record<string, string> = {
	'en-US': 'en',
	'zh-CN': 'zh-cn',
	'ms-MY': 'ms',
	'ta-IN': 'ta'
};

export const normalizeSupportedLanguage = (lang?: string | null) => {
	if (!lang) return DEFAULT_LANGUAGE;

	const exactMatch = SUPPORTED_LANGUAGE_CODES.find((code) => code.toLowerCase() === lang.toLowerCase());
	if (exactMatch) return exactMatch;

	const baseLanguage = lang.split('-')[0]?.toLowerCase();
	const partialMatch = SUPPORTED_LANGUAGE_CODES.find(
		(code) => code.split('-')[0]?.toLowerCase() === baseLanguage
	);

	return partialMatch ?? DEFAULT_LANGUAGE;
};

const applyLanguageSideEffects = (lang: string) => {
	if (typeof document !== 'undefined') {
		document.documentElement.setAttribute('lang', lang);
	}

	if (typeof localStorage !== 'undefined') {
		localStorage.setItem('locale', lang);
	}

	dayjs.locale(DAYJS_LANGUAGE_MAP[lang] ?? DAYJS_LANGUAGE_MAP[DEFAULT_LANGUAGE]);
};

const getInitialLanguage = (defaultLocale?: string) => {
	if (typeof window !== 'undefined') {
		const queryLocale = new URLSearchParams(window.location.search).get('lang');
		if (queryLocale) return normalizeSupportedLanguage(queryLocale);
	}

	if (defaultLocale) return normalizeSupportedLanguage(defaultLocale);

	if (typeof localStorage !== 'undefined') {
		const storedLocale = localStorage.getItem('locale');
		if (storedLocale) return normalizeSupportedLanguage(storedLocale);
	}

	if (typeof navigator !== 'undefined') {
		const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
		const browserMatch = browserLanguages.find(Boolean);
		if (browserMatch) return normalizeSupportedLanguage(browserMatch);
	}

	return DEFAULT_LANGUAGE;
};

const createI18nStore = (i18n: i18nType) => {
	const i18nWritable = writable(i18n);

	i18n.on('initialized', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('loaded', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('added', () => i18nWritable.set(i18n));
	i18n.on('languageChanged', (lang) => {
		i18nWritable.set(i18n);
		applyLanguageSideEffects(normalizeSupportedLanguage(lang));
	});
	return i18nWritable;
};

const createIsLoadingStore = (i18n: i18nType) => {
	const isLoading = writable(false);

	// if loaded resources are empty || {}, set loading to true
	i18n.on('loaded', (resources) => {
		// console.log('loaded:', resources);
		isLoading.set(Object.keys(resources).length === 0);
	});

	// if resources failed loading, set loading to true
	i18n.on('failedLoading', () => {
		isLoading.set(true);
	});

	return isLoading;
};

export const initI18n = async (defaultLocale?: string | undefined) => {
	const resolvedDefaultLocale = normalizeSupportedLanguage(defaultLocale);
	const initialLanguage = getInitialLanguage(defaultLocale);

	if (!i18next.isInitialized) {
		await i18next.init({
			debug: false,
			lng: initialLanguage,
			resources: TRANSLATION_RESOURCES,
			fallbackLng: [resolvedDefaultLocale],
			ns: 'translation',
			returnEmptyString: false,
			interpolation: {
				escapeValue: false // not needed for svelte as it escapes by default
			}
		});
	} else if (i18next.language !== initialLanguage) {
		await i18next.changeLanguage(initialLanguage);
	}

	await i18next.loadNamespaces(['translation']);
	const activeLanguage = normalizeSupportedLanguage(
		i18next.resolvedLanguage || i18next.language || initialLanguage
	);
	applyLanguageSideEffects(activeLanguage);
};

const i18n = createI18nStore(i18next);
const isLoadingStore = createIsLoadingStore(i18next);

export const getLanguages = async () => {
	const languages = (await import(`./locales/languages.json`)).default;
	return languages.filter((language) => SUPPORTED_LANGUAGE_CODES.includes(language.code));
};
export const changeLanguage = async (lang: string) => {
	const nextLanguage = normalizeSupportedLanguage(lang);
	applyLanguageSideEffects(nextLanguage);
	await i18next.changeLanguage(nextLanguage);
	await i18next.loadNamespaces(['translation']);
};

export default i18n;
export const isLoading = isLoadingStore;
